import React, { useState, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Stack,
    Dialog,
    Slide,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { FeeStructure, IEvent } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import CustomButton from '../Button/CustomButton';
import { getJWtDetails, getMinMaxFee, parseFee, useDispatchPromise } from '../../utils/AppUtils';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { isEmpty } from '../../utils/lodash';
import { TextWithIcon } from '../TextWithIcon/TextWithIcon';
import { LocalOffer } from '@mui/icons-material';
import { purchaseEventTickets } from '../../utils/checkout';
import { ICheckoutItem, TicketSummary } from '../../redux/checkout/types';
import { CustomPhoneTextField } from '../TextInput/CustomPhoneTextField';
import { purchaseTicket } from '../../redux/events/action';
import { removeDiscount } from '../../redux/checkout/action';
import { FeeCard } from '../FeeStructure/FeeCard';

// Default currency fallback
const DEFAULT_CURRENCY = 'usd';

interface IEventFeeAdjuster {
    event: IEvent;
    onClose?: () => void;
}

interface UserInfo {
    email: string;
    phone: string;
    country: string;
    name: string;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Gets the appropriate amount to display to the user
 * @param fee The fee structure containing amount information
 * @returns The amount to display
 */
const getDisplayAmount = (fee: FeeStructure): number => {
    // If bare_amount is available, use it (converted amount)
    if (typeof fee.bare_amount === 'number') {
        return fee.bare_amount;
    }

    // Otherwise parse the fee string
    return parseFee(fee.fee);
};

/**
 * Returns the max number of tickets a user can select for a given tier,
 * respecting both per-person limits and remaining stock.
 */
const getTierLimit = (fee: FeeStructure, eventMax?: number): number => {
    const remaining =
        typeof fee.available_qty === 'number' && typeof fee.total_sold === 'number'
            ? fee.available_qty - fee.total_sold
            : Infinity;
    const perPerson = fee.max_ticket_per_person ?? eventMax ?? Infinity;
    return Math.max(0, Math.min(remaining, perPerson));
};

const isTierSoldOut = (fee: FeeStructure): boolean => {
    if (typeof fee.available_qty === 'number' && typeof fee.total_sold === 'number') {
        return fee.total_sold >= fee.available_qty;
    }
    return false;
};

const EventFeeBox: React.FC<IEventFeeAdjuster> = ({ event, onClose }) => {
    const { fee_structure } = event || {};
    const { themeStyle, device } = useThemeContext();
    const [open, setOpen] = useState(false);
    const [freeTicketModalOpen, setFreeTicketModalOpen] = useState(false);
    const [selectedFreeFee, setSelectedFreeFee] = useState<FeeStructure | null>(null);
    const isSingleTicketSeller = fee_structure && fee_structure.length < 2 && Number(fee_structure[0].fee) === 0;

    const isEventClosed = useMemo(() => {
        if (!event.event_end_date) return false;
        return new Date(event.event_end_date).getTime() < Date.now();
    }, [event.event_end_date]);

    const [ticketQuantities, setTicketQuantities] = useState<Record<string, number>>({});
    const [, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [userInfo, setUserInfo] = useState<UserInfo>({ email: '', phone: '', country: '', name: '' });
    const [selectedFreeQuantity, setSelectedFreeQuantity] = useState<number>(1);
    const dispatch = useDispatchPromise();
    const { isSignedIn } = getJWtDetails();
    const [ticketSummary, setTicketSummary] = useState<TicketSummary>({
        paid: [],
        free: [],
        totalPaidAmount: 0,
        currency: DEFAULT_CURRENCY,
        totalAmountToPayInOriginalCurrency: 0,
        originalCurrency: DEFAULT_CURRENCY,
    });
    const maxTicketsAllowed = event?.max_ticket_purchasable_per_person || Infinity;

    const handleClose = () => {
        onClose && onClose();
        setOpen(false);
    };

    const handleFreeTicketModalClose = () => {
        setFreeTicketModalOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUserInfo(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const resetCount = () => {
        fee_structure?.forEach(fee => {
            setTicketQuantities(prev => ({
                ...prev,
                [fee._id]: 0,
            }));
        });
        setTotal(0);
        /** Reset Discount */
        dispatch(removeDiscount());
    };

    const handlePurchaseTicket = (items: ICheckoutItem[]) => {
        setLoading(true);
        dispatch(
            purchaseTicket({
                category: 'tickets',
                email: userInfo.email,
                items: items,
                linked_event: event._id,
                linked_event_type: 'Event',
                name: userInfo.name,
                phone: userInfo.phone,
                is_authenticated: isSignedIn,
            }),
        )
            .then(e => {
                setLoading(false);
                resetCount();
            })
            .catch(e => {
                setLoading(false);
                resetCount();
            });
    };

    const RenderSingleFee = ({ fee }: { fee: FeeStructure }) => {
        const tierLimit = getTierLimit(fee, maxTicketsAllowed);
        const soldOut = isTierSoldOut(fee);

        const incrementGoal = (): void => {
            const currentValue = ticketQuantities[fee._id] || 0;
            if (currentValue < tierLimit) {
                setTicketQuantities(prev => ({
                    ...prev,
                    [fee._id]: currentValue + 1,
                }));
                const displayAmount = getDisplayAmount(fee);
                setTotal(prevTotal => prevTotal + displayAmount);
            }
        };

        const decrementGoal = (): void => {
            const currentValue = ticketQuantities[fee._id] || 0;
            if (currentValue > 0) {
                setTicketQuantities(prev => ({
                    ...prev,
                    [fee._id]: currentValue - 1,
                }));
                const displayAmount = getDisplayAmount(fee);
                setTotal(prevTotal => prevTotal - displayAmount);
            }
        };

        const displayAmount = getDisplayAmount(fee);
        const isFree = displayAmount === 0;
        const isDiscounted = !isEmpty(fee.discount_message);

        return (
            <Box
                sx={{
                    padding: 2,
                    width: '100%',
                    borderBottom: 0.5,
                    borderBottomColor: theme[themeStyle].darkGray60,
                }}>
                <Box mb={2}>
                    <FeeCard
                        fee={{
                            ...fee,
                            description: fee.fee_features?.[0] || '',
                            amount: displayAmount,
                            type: typeof fee.fee === 'string' && fee.fee?.includes('%') ? 'percentage' : 'fixed',
                        }}
                    />
                </Box>

                <Stack direction="row" justifyContent="space-between" width={'100%'} alignItems="center" mt={2}>
                    <IconButton
                        onClick={decrementGoal}
                        disabled={!ticketQuantities[fee._id]}
                        sx={{
                            borderRadius: '50%',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                            width: '30px',
                            height: '30px',
                            border: `0.3px solid ${theme[themeStyle].tag_white}`,
                        }}>
                        <RemoveIcon sx={{ color: theme[themeStyle].tag_white }} />
                    </IconButton>
                    <Stack>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].tag_white,
                                fontSize: 60,
                            }}>
                            {ticketQuantities[fee._id] || 0}
                        </Typography>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: theme[themeStyle].tag_white,
                                fontSize: 14,
                            }}>
                            Ticket{(ticketQuantities[fee._id] || 0) > 1 && 's'}
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={incrementGoal}
                        disabled={soldOut || (ticketQuantities[fee._id] || 0) >= tierLimit}
                        sx={{
                            borderRadius: '50%',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                            width: '30px',
                            height: '30px',
                            border: `0.3px solid ${theme[themeStyle].tag_white}`,
                        }}>
                        <AddIcon sx={{ color: theme[themeStyle].tag_white }} />
                    </IconButton>
                </Stack>
                {soldOut && (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: theme[themeStyle].secondary_red,
                            textAlign: 'center',
                            mt: 2,
                        }}>
                        Sold Out
                    </Typography>
                )}
                {!isFree && !soldOut && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <CustomButton
                            label="Get Ticket"
                            customStyle={{ paddingTop: '8px', paddingBottom: '8px', borderRadius: '4px' }}
                            sx={{ width: '100%', my: 2 }}
                            onClick={() => handleOnPressPurchaseTicket()}
                        />
                    </Box>
                )}

                {isDiscounted && (
                    <TextWithIcon
                        text={fee.discount_message}
                        typographyProps={{
                            fontSize: 12,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].secondary_red,
                        }}
                        icon={
                            <LocalOffer
                                sx={{
                                    color: '#237EA2',
                                    fontSize: '18px',
                                    mr: 0.5,
                                }}
                                style={{
                                    transform: 'scaleX(-1)',
                                }}
                            />
                        }
                    />
                )}
            </Box>
        );
    };

    const handleOnPressPurchaseTicket = () => {
        if (isEventClosed) return;

        // RSVP-only events use the free-ticket / guest-info path (no separate Meeteor RSVP UI).
        if (event?.just_rsvp_required) {
            if (isSignedIn) {
                handleFreeTicketSubmit();
            } else if (fee_structure?.[0]) {
                setSelectedFreeFee(fee_structure[0]);
                setSelectedFreeQuantity(ticketQuantities[fee_structure[0]._id] || 1);
                setFreeTicketModalOpen(true);
            }
            return;
        }

        // For multiple tickets, open the modal to select quantities
        if (!isSingleTicketSeller) {
            setOpen(true);
            return;
        }

        // For single ticket and free, open the user info collection modal
        if (isSingleTicketSeller && fee_structure && parseFee(fee_structure[0].fee) === 0) {
            if (isSignedIn) {
                handleFreeTicketSubmit();
            } else {
                setSelectedFreeFee(fee_structure[0]);
                setSelectedFreeQuantity(ticketQuantities[fee_structure[0]._id] || 1);
                setFreeTicketModalOpen(true);
            }
            return;
        }

        // For single paid ticket
        if (isSingleTicketSeller && fee_structure) {
            processPurchase();
        }
    };

    const handleFreeTicketSubmit = () => {
        // Here you would typically send this data to your backend
        setFreeTicketModalOpen(false);
        /** Build the object here */
        const ticketSummary: ICheckoutItem[] = [];
        fee_structure?.forEach(fee => {
            const quantity = ticketQuantities[fee._id] || (isSingleTicketSeller ? 1 : 0);
            // Skip tickets that have quantity 0
            if (quantity <= 0) return;
            const load: ICheckoutItem = {
                id: fee._id,
                tierId: fee._id,
                currency: fee.currency,
                quantity: quantity,
                original_price: fee.original_price,
                price: 0,
                title: fee.fee_name,
                description: '',
            };
            ticketSummary.push(load);
        });
        handlePurchaseTicket(ticketSummary);
    };

    const processPurchase = () => {
        // Create a summary of all selected tickets
        const summary: TicketSummary = {
            paid: [],
            free: [],
            totalPaidAmount: 0,
            currency: DEFAULT_CURRENCY,
            totalAmountToPayInOriginalCurrency: 0,
            originalCurrency: DEFAULT_CURRENCY,
        };

        // Track the primary currency for the transaction
        let primaryCurrency = '';
        let currencyInfo:
            | {
                  transaction_currency: string;
                  original_currency?: string;
                  rate?: number;
              }
            | undefined = undefined;

        // Process all tickets by type
        fee_structure?.forEach(fee => {
            const quantity = ticketQuantities[fee._id] || (isSingleTicketSeller ? 1 : 0);

            // Skip tickets that have quantity 0
            if (quantity <= 0) return;

            // const displayCurrency = getDisplayCurrency(fee);

            // Set the primary transaction currency based on the first item
            if (!primaryCurrency) {
                const txCurrency =
                    fee.transaction_currency || fee.converted_currency || fee.currency || DEFAULT_CURRENCY;
                primaryCurrency = txCurrency;
                summary.currency = primaryCurrency;
                summary.originalCurrency = String(fee.original_currency || fee.currency || DEFAULT_CURRENCY);
                // Create currency info object — fallback converted_currency → currency when transaction_currency is absent
                currencyInfo = {
                    transaction_currency: txCurrency,
                    original_currency: fee.original_currency || fee.currency || DEFAULT_CURRENCY,
                    rate: fee.rate ?? 1,
                };
            }

            if (parseFee(fee.fee) === 0) {
                // Handle free tickets
                summary.free.push({
                    id: fee._id,
                    tierId: fee._id,
                    name: fee.fee_name,
                    quantity: quantity,
                    features: fee.fee_features,
                    description: '',
                    original_price: 0,
                    price: 0,
                });
            } else {
                // Handle paid tickets
                // Get the proper amount for calculation
                // const displayAmount = getDisplayAmount(fee);

                // For the checkout system, we need to use the original currency amount
                // This might need adjustment based on your payment processor requirements
                const price = fee.bare_amount || parseFloat(String(fee.fee).replace(/[^0-9.]/g, ''));
                const price_to_process_from_original_currency = fee.original_price;

                summary.paid.push({
                    id: fee._id,
                    tierId: fee._id,
                    name: fee.fee_name,
                    price: price,
                    quantity: quantity,
                    description: fee.fee_features.join(','),
                    currency: fee.original_currency || DEFAULT_CURRENCY,
                    original_currency: fee.original_currency,
                    original_price: fee.original_price,
                    transaction_currency: fee.transaction_currency!,
                    exchange_rate: fee.rate!,
                });

                // Keep track of total in the display currency
                summary.totalPaidAmount += price * quantity;
                summary.totalAmountToPayInOriginalCurrency +=
                    Number(price_to_process_from_original_currency) * quantity;
            }
        });

        setTicketSummary(summary);

        // If we have both paid and free tickets, or just paid tickets, process payment
        if (summary.paid.length > 0) {
            // Add currency information to each ticket item
            const paidItemsWithCurrency = summary.paid.map(item => ({
                ...item,
                currency: summary.currency,
            }));

            purchaseEventTickets(
                {
                    id: event._id,
                    title: event.name,
                    image: event.feature_image,
                },
                paidItemsWithCurrency,
                {
                    category: 'tickets',
                    item: paidItemsWithCurrency,
                    linked_event: event._id,
                    linked_event_type: 'Event',
                    transaction_currency: ticketSummary.currency,
                    max_items: event.max_ticket_purchasable_per_person ?? Infinity,
                },
                currencyInfo,
            );

            // If we also have free tickets, show free ticket modal after payment
            if (summary.free.length > 0) {
                const freeFee = fee_structure?.find(fee => fee._id === summary.free[0].id);
                if (freeFee) {
                    setSelectedFreeFee(freeFee);
                    setSelectedFreeQuantity(summary.free[0].quantity);
                    // We'll show the free ticket modal after payment completion
                    // This would be handled by a callback from the payment process
                }
            }

            setOpen(false);
        }
        // If we only have free tickets, show the free ticket modal
        else if (summary.free.length > 0) {
            const freeFee = fee_structure?.find(fee => fee._id === summary.free[0].id);
            if (freeFee) {
                setSelectedFreeFee(freeFee);
                setSelectedFreeQuantity(summary.free[0].quantity);
                setFreeTicketModalOpen(true);
            }
            setOpen(false);
        } else {
            // No tickets selected
            setOpen(false);
        }
        resetCount();
    };

    // Memoize ticket selection handlers to prevent unnecessary re-renders
    const handleMultipleTicketSelection = useCallback(
        (fee: FeeStructure, increment: boolean) => {
            const tierMax = getTierLimit(fee, maxTicketsAllowed);

            setTicketQuantities(prev => {
                const currentTotal = Object.values(prev).reduce((sum, qty) => sum + qty, 0);
                const currentQty = prev[fee._id] || 0;

                if (increment && currentTotal < maxTicketsAllowed && currentQty < tierMax) {
                    return { ...prev, [fee._id]: currentQty + 1 };
                } else if (!increment && currentQty > 0) {
                    return { ...prev, [fee._id]: currentQty - 1 };
                }
                return prev;
            });
        },
        [maxTicketsAllowed],
    );

    // Memoize the FreeTicketModal to prevent unnecessary re-renders
    const FreeTicketModal = useMemo(() => {
        // Show all selected free tickets if there are multiple
        const multipleFreeFees = ticketSummary.free.length > 1;

        return (
            <Dialog
                scroll="paper"
                open={freeTicketModalOpen}
                onClose={handleFreeTicketModalClose}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    style: {
                        backgroundColor: theme[themeStyle].form_background,
                        borderRadius: 8,
                        padding: 16,
                    },
                }}
                keepMounted>
                <DialogTitle>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Typography
                            style={{
                                fontSize: 24,
                                fontWeight: '600',
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            Complete Your Free Registration
                        </Typography>
                        <CloseIcon
                            onClick={handleFreeTicketModalClose}
                            style={{ cursor: 'pointer' }}
                            stroke={theme[themeStyle].tag_white}
                        />
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: 16,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            mb: 3,
                        }}>
                        Please provide your contact information to register for your free{' '}
                        {multipleFreeFees ? 'tickets' : 'ticket'}:
                    </Typography>

                    {/* Display information about the selected free tickets */}
                    {selectedFreeFee && !multipleFreeFees && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 18,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}>
                                    {selectedFreeFee.fee_name}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme[themeStyle].secondary_red,
                                        fontSize: 18,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                    }}>
                                    {selectedFreeQuantity} {selectedFreeQuantity > 1 ? 'Tickets' : 'Ticket'}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 14,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                Free Ticket
                            </Typography>
                            {selectedFreeFee.fee_features.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 14,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                        }}>
                                        Includes:
                                    </Typography>
                                    {selectedFreeFee.fee_features.map((feature, index) => (
                                        <Typography
                                            key={index}
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontSize: 12,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            }}>
                                            • {feature}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Display information about multiple free tickets */}
                    {multipleFreeFees && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                            <Typography
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 18,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    mb: 2,
                                }}>
                                Free Tickets Summary
                            </Typography>

                            {ticketSummary.free.map((freeFee, index) => {
                                const feeDetails = fee_structure?.find(f => f._id === freeFee.id);
                                return (
                                    <Box key={freeFee.id} sx={{ mb: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontSize: 16,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {freeFee.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].secondary_red,
                                                    fontSize: 16,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                }}>
                                                {freeFee.quantity} {freeFee.quantity > 1 ? 'Tickets' : 'Ticket'}
                                            </Typography>
                                        </Box>

                                        {feeDetails && feeDetails.fee_features.length > 0 && (
                                            <Box sx={{ mt: 0.5 }}>
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontSize: 12,
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    }}>
                                                    Includes:
                                                </Typography>
                                                {feeDetails.fee_features.map((feature, idx) => (
                                                    <Typography
                                                        key={idx}
                                                        sx={{
                                                            color: theme[themeStyle].tag_white,
                                                            fontSize: 11,
                                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                        }}>
                                                        • {feature}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}

                                        {index < ticketSummary.free.length - 1 && (
                                            <Divider
                                                sx={{
                                                    my: 1.5,
                                                    borderColor: 'rgba(255,255,255,0.1)',
                                                }}
                                            />
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        margin="normal"
                        name="name"
                        label="Fullname"
                        variant="outlined"
                        value={userInfo.name}
                        onChange={handleInputChange}
                        InputProps={{
                            style: {
                                color: theme[themeStyle].tag_white,
                            },
                        }}
                        InputLabelProps={{
                            style: {
                                color: theme[themeStyle].grey,
                            },
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: theme[themeStyle].grey,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].tag_white,
                                },
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        name="email"
                        label="Email"
                        variant="outlined"
                        value={userInfo.email}
                        onChange={handleInputChange}
                        InputProps={{
                            style: {
                                color: theme[themeStyle].tag_white,
                            },
                        }}
                        InputLabelProps={{
                            style: {
                                color: theme[themeStyle].grey,
                            },
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: theme[themeStyle].grey,
                                },
                                '&:hover fieldset': {
                                    borderColor: theme[themeStyle].tag_white,
                                },
                            },
                        }}
                    />
                    <CustomPhoneTextField
                        value={userInfo.phone}
                        customStyle={{ backgroundColor: 'transparent' }}
                        onChange={(e, data) => {
                            setUserInfo(prev => ({
                                ...prev,
                                phone: e,
                                country: data.countryCode,
                            }));
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <CustomButton
                        label="Register Now"
                        sx={{ width: '100%', mt: 2 }}
                        customStyle={{ borderRadius: '4px' }}
                        onClick={handleFreeTicketSubmit}
                        disabled={loading || !userInfo.email || !userInfo.phone || !userInfo.name}
                        loading={loading}
                    />
                </DialogActions>
            </Dialog>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        freeTicketModalOpen,
        handleFreeTicketModalClose,
        themeStyle,
        selectedFreeFee,
        selectedFreeQuantity,
        userInfo.email,
        userInfo.phone,
        handleInputChange,
        handleFreeTicketSubmit,
        ticketSummary.free,
    ]);

    return (
        <React.Fragment>
            {isSingleTicketSeller ? (
                fee_structure.map(fee => <RenderSingleFee fee={fee} key={fee._id} />)
            ) : (
                <Stack width={'100%'} alignItems="center" my={3}>
                    <Typography fontFamily={FONT_ENUM.MAIN_BOLD} fontSize={32} color={theme[themeStyle].tag_white}>
                        {getMinMaxFee(fee_structure!).min_fee} - {getMinMaxFee(fee_structure!).max_fee}
                    </Typography>
                </Stack>
            )}

            {/* Extract the dialog from RenderMultipleTickets */}
            <Dialog
                scroll="paper"
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                fullWidth={true}
                fullScreen={true}
                maxWidth={'lg'}
                PaperProps={{
                    style: {
                        backgroundColor: theme[themeStyle].pure_black,
                        width: '100vw',
                        maxWidth: device.isMobile ? '100vw' : '500px',
                        borderRadius: 0,
                        border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                        margin: 0,
                        height: device.isMobile ? '100dvh' : '80%',
                        backdropFilter: 'blur(20px)',
                    },
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                }}>
                <DialogTitle>
                    <Box
                        sx={{
                            width: '100%',
                            height: 'fit-content',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Typography
                            style={{
                                textAlign: 'left',
                                fontSize: 24,
                                fontStyle: 'normal',
                                fontWeight: '600',
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            Select Tickets
                        </Typography>
                        <CloseIcon
                            onClick={handleClose}
                            style={{ cursor: 'pointer' }}
                            stroke={theme[themeStyle].tag_white}
                        />
                    </Box>
                </DialogTitle>
                {event.refund_policy === 'non-refundable' && (
                    <DialogTitle>
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}>
                            <Typography
                                style={{
                                    textAlign: 'left',
                                    fontSize: 12,
                                    fontStyle: 'normal',
                                    fontWeight: '600',
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                All purchases are final and any refunds MUST be requested via the host
                            </Typography>
                        </Box>
                    </DialogTitle>
                )}
                <DialogContent>
                    {fee_structure &&
                        fee_structure.map(fee => {
                            const displayAmount = getDisplayAmount(fee);
                            const tierMax = getTierLimit(fee, maxTicketsAllowed);
                            const soldOut = isTierSoldOut(fee);

                            return (
                                <Box
                                    key={fee._id}
                                    sx={{
                                        padding: 2,
                                        width: '100%',
                                        borderBottom: 0.5,
                                        borderBottomColor: theme[themeStyle].darkGray60,
                                        opacity: soldOut ? 0.5 : 1,
                                    }}>
                                    <FeeCard
                                        fee={{
                                            ...fee,
                                            description: fee.fee_features?.[0] || '',
                                            amount: displayAmount,
                                            type:
                                                typeof fee.fee === 'string' && fee.fee?.includes('%')
                                                    ? 'percentage'
                                                    : 'fixed',
                                        }}
                                    />

                                    {soldOut && (
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 14,
                                                color: theme[themeStyle].secondary_red,
                                                textAlign: 'center',
                                                mt: 2,
                                            }}>
                                            Sold Out
                                        </Typography>
                                    )}

                                    {!isEmpty(fee.discount_message) && (
                                        <TextWithIcon
                                            text={fee.discount_message}
                                            typographyProps={{
                                                fontSize: 12,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].secondary_red,
                                                mt: 1,
                                            }}
                                            icon={
                                                <LocalOffer
                                                    sx={{
                                                        color: '#237EA2',
                                                        fontSize: '18px',
                                                        mr: 0.5,
                                                    }}
                                                    style={{
                                                        transform: 'scaleX(-1)',
                                                    }}
                                                />
                                            }
                                        />
                                    )}

                                    {!soldOut && (
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            width={'100%'}
                                            alignItems="center"
                                            mt={2}>
                                            <IconButton
                                                onClick={() => handleMultipleTicketSelection(fee, false)}
                                                disabled={!ticketQuantities[fee._id]}
                                                sx={{
                                                    borderRadius: '50%',
                                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                                    width: '30px',
                                                    height: '30px',
                                                    border: `0.3px solid ${theme[themeStyle].tag_white}`,
                                                }}>
                                                <RemoveIcon sx={{ color: theme[themeStyle].tag_white }} />
                                            </IconButton>
                                            <Stack>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        color: theme[themeStyle].tag_white,
                                                        fontSize: 60,
                                                    }}>
                                                    {ticketQuantities[fee._id] || 0}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        color: theme[themeStyle].tag_white,
                                                        fontSize: 14,
                                                    }}>
                                                    Ticket{(ticketQuantities[fee._id] || 0) > 1 && 's'}
                                                </Typography>
                                            </Stack>
                                            <IconButton
                                                onClick={() => handleMultipleTicketSelection(fee, true)}
                                                disabled={(ticketQuantities[fee._id] || 0) >= tierMax}
                                                sx={{
                                                    borderRadius: '50%',
                                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                                                    width: '30px',
                                                    height: '30px',
                                                    border: `0.3px solid ${theme[themeStyle].tag_white}`,
                                                }}>
                                                <AddIcon sx={{ color: theme[themeStyle].tag_white }} />
                                            </IconButton>
                                        </Stack>
                                    )}
                                </Box>
                            );
                        })}
                </DialogContent>
                <DialogActions>
                    <CustomButton
                        label="Get Tickets"
                        sx={{ width: '100%', pt: 4 }}
                        customStyle={{ borderRadius: '4px' }}
                        onClick={processPurchase}
                    />
                </DialogActions>
            </Dialog>

            {FreeTicketModal}
            <CustomButton
                label={
                    isEventClosed
                        ? 'Event Closed'
                        : isSingleTicketSeller || event?.just_rsvp_required
                        ? 'RSVP'
                        : 'Get Tickets'
                }
                sx={{ width: '100%', mt: 1 }}
                customStyle={{ borderRadius: '4px' }}
                onClick={handleOnPressPurchaseTicket}
                disabled={isEventClosed || loading}
                loading={loading}
            />
        </React.Fragment>
    );
};

export { EventFeeBox };
